import {
  DocumentExcelPane,
  DocumentPdfPane,
  DocumentViewer,
  type DocumentPaneRenderProps,
} from '@aecom-ai-engineering/react-components';
import {
  useGetBlobQuery,
  useGetBlobReadUrlQuery,
  usePackageId,
  useProjectId,
} from '@consigli/hooks';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentLoadingPane } from '@/features/document-viewer/document-loading-pane';
import { toExcelHighlight, toHighlights } from '@/features/document-viewer/excel-highlight';
import { ScheduleContextSidebar } from '@/features/document-viewer/schedule-context-sidebar';
import {
  ComponentReference,
  ScheduleComponent,
} from '@/pages/schedule-check/components/component-card';
import { isExcelDocumentType } from '@/util/document-check';

interface ScheduleSingleViewerProps {
  schedule: ComponentReference;
  component: ScheduleComponent;
  isPreview: boolean;
  onClose?: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

export const ScheduleSingleViewer: FC<ScheduleSingleViewerProps> = ({
  schedule,
  component,
  onClose,
  onNext,
  onPrevious,
  hasNext = false,
  hasPrevious = false,
}) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();

  const { data: blob } = useGetBlobQuery(
    { projectId, packageId, blobId: schedule.blobId! },
    { skip: !schedule.blobId },
  );

  const { data: readUrlData } = useGetBlobReadUrlQuery(
    { projectId, packageId, blobId: schedule.blobId! },
    { skip: !schedule.blobId },
  );

  const isExcel = !!schedule.fileName && isExcelDocumentType(schedule.fileName);
  const src = isExcel ? (blob?.downloadUrl ?? '') : (readUrlData?.readUrl ?? '');
  const excelHighlight = useMemo(
    () => (isExcel ? toExcelHighlight(schedule.cellCoordinates) : undefined),
    [isExcel, schedule.cellCoordinates],
  );
  const initialPage = isExcel ? undefined : (schedule.page ?? undefined);
  // Draw the finding's bounding box on the PDF pane. `page` is already 1-based
  // (parseFileMetadata adds 1), while toHighlights expects a 0-based pageIndex
  // and re-adds 1, so hand it `page - 1` to land on the right page.
  const pdfHighlights = useMemo(
    () =>
      !isExcel && schedule.coordinates && schedule.page
        ? toHighlights({ ...schedule.coordinates, pageIndex: schedule.page - 1 })
        : undefined,
    [isExcel, schedule.coordinates, schedule.page],
  );

  // Swapping `src` on a live AECOM pane can wedge it on "Rendering page"; keying
  // by document + page remounts it on navigation.
  const paneKey = `${schedule.blobId ?? ''}#${schedule.page ?? ''}`;

  // While the signed URL resolves `props.src` is empty; render a spinner in the
  // pane so the drawer opens immediately instead of flashing a loader at the
  // bottom of the page.
  const renderers = useMemo(
    () => ({
      excel: (props: DocumentPaneRenderProps) =>
        props.src ? (
          <DocumentExcelPane
            key={paneKey}
            active={props.active}
            caption={props.caption}
            fileName=""
            highlight={excelHighlight}
            searchable
            src={props.src}
          />
        ) : (
          <DocumentLoadingPane caption={props.caption} />
        ),
      pdf: (props: DocumentPaneRenderProps) =>
        props.src ? (
          <DocumentPdfPane
            key={paneKey}
            active={props.active}
            caption={props.caption}
            onSidebarContextChange={props.onSidebarContextChange}
            src={props.src}
            initialPage={initialPage}
            highlights={pdfHighlights}
            searchable
          />
        ) : (
          <DocumentLoadingPane caption={props.caption} />
        ),
    }),
    [excelHighlight, initialPage, pdfHighlights, paneKey],
  );

  const scheduleLabel = schedule.fileName ?? t('schedule-double-viewer.schedule-pane-label');

  return (
    <DocumentViewer
      open
      onOpenChange={(details) => {
        if (!details.open) onClose?.();
      }}
      src={src}
      fileType={isExcel ? 'excel' : undefined}
      fileName={schedule.fileName}
      title={scheduleLabel}
      headerLabel={component.name}
      showSidebar
      showNavigationActions
      previousDisabled={!hasPrevious}
      nextDisabled={!hasNext}
      onPrevious={onPrevious}
      onNext={onNext}
      renderers={renderers}
    >
      <ScheduleContextSidebar component={component} />
    </DocumentViewer>
  );
};
